import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Users } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function TaskFormDialog({ open, onOpenChange, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [groupMode, setGroupMode] = useState(false);
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", assignee_id: "", client_id: "", priority: "Medium", status: "To Do",
  });
  const [deadline, setDeadline] = useState(null);

  useEffect(() => {
    if (open) {
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
      setForm({ title: "", description: "", assignee_id: "", client_id: "", priority: "Medium", status: "To Do" });
      setDeadline(null);
      setGroupMode(false);
      setAssigneeIds([]);
    }
  }, [open]);

  const toggleAssignee = (id) => {
    setAssigneeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async () => {
    if (!form.title || (groupMode ? assigneeIds.length === 0 : !form.assignee_id)) {
      toast.error(groupMode ? "Task title and at least one assignee are required" : "Task title and assignee are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/tasks", {
        ...form,
        assignee_id: groupMode ? null : form.assignee_id,
        assignee_ids: groupMode ? assigneeIds : null,
        client_id: form.client_id || null,
        deadline: deadline ? format(deadline, "yyyy-MM-dd") : null,
      });
      const emailFailed = groupMode ? (res.data.email_failures || []).length > 0 : res.data.email_sent === false;
      if (emailFailed) {
        toast.success(groupMode ? `Task allocated to ${assigneeIds.length} employees` : "Task allocated successfully");
        toast.warning("Note: assignment email could not be delivered to one or more employees. They can still see the task on their Task Board.");
      } else {
        toast.success(groupMode ? `Task allocated to ${assigneeIds.length} employees` : "Task allocated successfully");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Allocate New Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Task Name</Label>
            <Input
              data-testid="task-form-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Draft ESG audit report"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              data-testid="task-form-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Task details / deliverable expectations"
              rows={3}
            />
          </div>

          <button
            type="button"
            data-testid="task-form-group-toggle"
            onClick={() => { setGroupMode(!groupMode); setAssigneeIds([]); setForm({ ...form, assignee_id: "" }); }}
            className={cn(
              "flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors duration-200",
              groupMode ? "bg-[#F7FEE7] border-[#76C121] text-[#00823B]" : "border-[#E2E8F0] text-[#4B5563]"
            )}
          >
            <Users className="h-3.5 w-3.5" /> {groupMode ? "Group allocation ON — assign to multiple employees" : "Assign to multiple employees (group task)"}
          </button>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{groupMode ? "Assignees" : "Assignee"}</Label>
              {groupMode ? (
                <div className="border border-[#E2E8F0] rounded-md p-2 max-h-32 overflow-y-auto space-y-1.5" data-testid="task-form-assignee-checklist">
                  {employees.map((emp) => (
                    <label key={emp.id} className="flex items-center gap-2 text-sm px-1 py-0.5 cursor-pointer">
                      <Checkbox data-testid={`task-form-assignee-checkbox-${emp.id}`} checked={assigneeIds.includes(emp.id)} onCheckedChange={() => toggleAssignee(emp.id)} />
                      {emp.name}
                    </label>
                  ))}
                </div>
              ) : (
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger data-testid="task-form-assignee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-form-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Related Client (optional)</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="task-form-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Deadline</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="task-form-deadline-trigger"
                    className={cn("w-full justify-start text-left font-normal", !deadline && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {deadline ? format(deadline, "PP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={deadline} onSelect={setDeadline} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="task-form-cancel-button">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="task-form-submit-button"
            className="bg-[#76C121] hover:bg-[#5C9819] text-white"
          >
            {submitting ? "Allocating..." : "Allocate Task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
